import { FC, useContext, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Loader2, MoreVertical, Plus, Trash2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { AuthContext } from '@/context/auth-context';
import { useListTriggerConnections } from '@/hooks/queries/triggers/use-list-trigger-connections';
import { useListTriggers } from '@/hooks/queries/triggers/use-list-triggers';
import { useListTriggerSubscriptions } from '@/hooks/queries/triggers/use-list-trigger-subscriptions';
import {
  useDeleteTrigger,
  useDeleteTriggerSubscription,
  useSetTriggerEnabled,
} from '@/hooks/mutations/triggers/use-trigger-mutations';
import { Trigger } from '@/services/triggers/types';
import { AddTriggerModal } from '@/pages/triggers/providers/google-drive/add-trigger-modal';
import { useConnectGoogleDrive } from '@/pages/triggers/use-connect-google-drive';
import { isConnectionUsable } from '@/pages/triggers/connection-state';
import { cn } from '@/lib/utils';

interface TriggersConfigSectionProps {
  workflowId: string;
  workflowName?: string;
  isVisible: boolean;
  // 'section' renders inside the workflow-editor sidebar; 'modal' drops the
  // outer chrome so it sits under the shared Modal's own header.
  variant?: 'section' | 'modal';
  onChanged?: () => void;
}

/**
 * This workflow's triggers, and a way to add one.
 *
 * Scoped to this workflow on purpose: attaching a trigger to *other* workflows
 * belongs on the Triggers page, since listing other workflows from inside one
 * makes no sense. Triggers on a colleague's Google account appear here — the
 * workflow is shared, so hiding them would leave an editor unable to explain why
 * it runs — but with their folder redacted by the API, and detach as the only
 * action available.
 */
export const TriggersConfigSection: FC<TriggersConfigSectionProps> = ({
  workflowId,
  workflowName,
  isVisible,
  variant = 'section',
  onChanged,
}) => {
  const { currentOrg } = useOrgStore();
  const { user } = useContext(AuthContext);
  const notification = useNotification();
  const orgId = currentOrg?.orgid || '';
  const myEmail = user?.email;

  const [isAdding, setIsAdding] = useState(false);
  const [detachTarget, setDetachTarget] = useState<Trigger | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Trigger | null>(null);

  // Both are live reads; don't spend them while the Configure tab is closed.
  const connectionsQuery = useListTriggerConnections(orgId, isVisible);
  const triggersQuery = useListTriggers(orgId, workflowId, isVisible);
  const setEnabled = useSetTriggerEnabled(orgId);
  const removeSubscription = useDeleteTriggerSubscription(orgId);
  const { connect, reconnect, isConnecting } = useConnectGoogleDrive(
    orgId,
    myEmail,
  );

  const triggers = useMemo(
    () => triggersQuery.data ?? [],
    [triggersQuery.data],
  );

  // My Drive connection whatever state it is in. Matching only usable ones
  // would make an expired or half-authorized connection look absent, and
  // "Connect" would then create a second one instead of repairing this.
  const myConnection = useMemo(
    () =>
      (connectionsQuery.data ?? []).find(
        (c) =>
          c.provider === 'google_drive' &&
          c.auth_backend === 'managed' &&
          !!myEmail &&
          c.config?.connected_by_email === myEmail,
      ),
    [connectionsQuery.data, myEmail],
  );

  // Only these can actually reach Drive; anything else needs consent again
  // before a folder can be watched.
  const isUsable = !!myConnection && isConnectionUsable(myConnection);

  const isLoading = connectionsQuery.isLoading || triggersQuery.isLoading;

  // Repair the existing connection rather than creating a second one for the
  // same Google account.
  const handleConnect = () =>
    myConnection ? reconnect(myConnection.id) : connect();

  const handleToggle = async (trigger: Trigger, enabled: boolean) => {
    try {
      await setEnabled.mutateAsync({ triggerId: trigger.id, enabled });
      onChanged?.();
    } catch (error) {
      Sentry.captureException(error, { extra: { triggerId: trigger.id } });
      notification.error(
        error instanceof Error ? error.message : 'Could not update the trigger',
      );
    }
  };

  return (
    <div className={cn(variant === 'section' && 'p-4 border-b')}>
      <div className='flex items-center justify-between'>
        <span
          className={cn(
            'text-sm font-medium',
            variant === 'modal' && 'text-muted-foreground',
          )}
        >
          {variant === 'modal' ? '' : 'Triggers'}
        </span>
        {isUsable ? (
          <Button variant='ghost' size='sm' onClick={() => setIsAdding(true)}>
            Add Trigger
            <Plus className='size-4' />
          </Button>
        ) : (
          <Button
            variant='ghost'
            size='sm'
            onClick={handleConnect}
            disabled={isConnecting}
          >
            {isConnecting && <Loader2 className='mr-2 size-4 animate-spin' />}
            {myConnection ? 'Finish authorization' : 'Connect Google Drive'}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className='mt-2 flex items-center gap-2 p-3'>
          <Skeleton className='size-9 shrink-0 rounded-md' />
          <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
            <Skeleton className='h-4 w-1/2' />
            <Skeleton className='h-3 w-3/4' />
          </div>
        </div>
      ) : triggers.length === 0 ? (
        <div className='mt-2 flex items-center gap-2 rounded-md border-2 border-dashed p-3'>
          <div className='flex size-9 shrink-0 items-center justify-center rounded-md bg-accent'>
            <Zap className='size-5 text-muted-foreground' />
          </div>
          <div className='min-w-0'>
            <p className='text-sm font-medium'>No triggers yet</p>
            <p className='text-sm text-muted-foreground'>
              {isUsable
                ? 'Run this workflow when a file lands in a Drive folder'
                : myConnection
                  ? 'Your Google Drive connection needs authorizing again'
                  : 'Connect your Google Drive to watch a folder'}
            </p>
          </div>
        </div>
      ) : (
        <div className='mt-1 flex flex-col gap-1'>
          {triggers.map((trigger) => (
            <TriggerLine
              key={trigger.id}
              trigger={trigger}
              onToggle={handleToggle}
              onRequestDetach={setDetachTarget}
              onRequestDelete={setDeleteTarget}
              isBusy={setEnabled.isPending}
            />
          ))}
        </div>
      )}

      {myConnection && isUsable && (
        <AddTriggerModal
          open={isAdding}
          onOpenChange={(open) => {
            setIsAdding(open);
            if (!open) onChanged?.();
          }}
          orgId={orgId}
          connection={myConnection}
          lockedWorkflowId={workflowId}
          lockedWorkflowName={workflowName}
        />
      )}

      <DetachModal
        trigger={detachTarget}
        orgId={orgId}
        workflowId={workflowId}
        isPending={removeSubscription.isPending}
        onClose={() => setDetachTarget(null)}
        onDetached={() => {
          setDetachTarget(null);
          onChanged?.();
        }}
      />

      <DeleteTriggerModal
        trigger={deleteTarget}
        orgId={orgId}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          onChanged?.();
        }}
      />
    </div>
  );
};

interface TriggerLineProps {
  trigger: Trigger;
  isBusy: boolean;
  onToggle: (trigger: Trigger, enabled: boolean) => void;
  onRequestDetach: (trigger: Trigger) => void;
  onRequestDelete: (trigger: Trigger) => void;
}

const TriggerLine = ({
  trigger,
  isBusy,
  onToggle,
  onRequestDetach,
  onRequestDelete,
}: TriggerLineProps) => {
  const isMine = !trigger.redacted;
  const folder = trigger.config?.folder_name || trigger.config?.folder_id;

  return (
    <div className='flex items-center gap-2 rounded-md p-2 hover:bg-accent/50'>
      <span
        className={cn(
          'inline-block size-[7px] shrink-0 rounded-full',
          trigger.enabled ? 'animate-pulse bg-emerald-500' : 'bg-amber-400',
        )}
      />
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-medium'>
          {isMine ? folder || 'Drive folder' : 'Google Drive'}
        </p>
        <p className='truncate text-xs text-muted-foreground'>
          {isMine
            ? 'Google Drive'
            : `Connected by ${trigger.owner_email ?? 'another member'}`}
        </p>
      </div>
      {isMine && (
        <Switch
          checked={trigger.enabled}
          disabled={isBusy}
          onCheckedChange={(checked) => onToggle(trigger, checked)}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='size-8'
            aria-label='Trigger actions'
          >
            <MoreVertical className='size-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          {/* Detach first: it only affects this workflow, so it is the safe
              default from inside one. */}
          <DropdownMenuItem onClick={() => onRequestDetach(trigger)}>
            Remove from this workflow
          </DropdownMenuItem>
          {isMine && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className='text-destructive'
                onClick={() => onRequestDelete(trigger)}
              >
                <Trash2 className='mr-2 size-4' />
                Delete trigger
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface DetachModalProps {
  trigger: Trigger | null;
  orgId: string;
  workflowId: string;
  isPending: boolean;
  onClose: () => void;
  onDetached: () => void;
}

/**
 * Detaching needs the subscription id, which only the trigger's own
 * subscription list carries — so it is fetched once the user opts in rather
 * than for every row on render.
 */
const DetachModal = ({
  trigger,
  orgId,
  workflowId,
  isPending,
  onClose,
  onDetached,
}: DetachModalProps) => {
  const notification = useNotification();
  const removeSubscription = useDeleteTriggerSubscription(orgId);
  const subscriptionsQuery = useListTriggerSubscriptions(orgId, trigger?.id);

  const handleDetach = async () => {
    if (!trigger || subscriptionsQuery.isLoading) return;
    const sub = (subscriptionsQuery.data ?? []).find(
      (s) => s.target_ref === workflowId,
    );
    if (!sub) {
      notification.error('Could not find this workflow’s trigger binding');
      return;
    }
    try {
      await removeSubscription.mutateAsync({
        triggerId: trigger.id,
        subscriptionId: sub.id,
      });
      notification.success?.('Trigger detached from this workflow');
      onDetached();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { triggerId: trigger.id, workflowId },
      });
      notification.error(
        error instanceof Error ? error.message : 'Could not detach the trigger',
      );
    }
  };

  return (
    <DeleteConfirmationModal
      isOpen={!!trigger}
      handleCancel={onClose}
      handleDelete={handleDetach}
      // Includes the subscriptions read: the binding id comes from it, so
      // confirming before it lands would report "binding not found" wrongly.
      isLoading={
        isPending ||
        removeSubscription.isPending ||
        subscriptionsQuery.isLoading
      }
      buttonLabel='Detach'
      title='Detach trigger'
      description='This workflow will stop running when files arrive. The trigger itself keeps running for any other workflows attached to it.'
    />
  );
};

interface DeleteTriggerModalProps {
  trigger: Trigger | null;
  orgId: string;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Deleting reaches past this workflow — the trigger may feed others — so the
 * confirmation says how many, which is the one fact that makes it a considered
 * choice rather than a surprise.
 */
const DeleteTriggerModal = ({
  trigger,
  orgId,
  onClose,
  onDeleted,
}: DeleteTriggerModalProps) => {
  const notification = useNotification();
  const removeTrigger = useDeleteTrigger(orgId);
  const subscriptionsQuery = useListTriggerSubscriptions(orgId, trigger?.id);
  // A failed read leaves the count unknown, which must not read as "no others":
  // the fallback of 1 exists to mean "this workflow's own binding", so without
  // it the copy would promise a blast radius it never confirmed.
  const countUnknown =
    subscriptionsQuery.isPending || subscriptionsQuery.isError;
  const others = Math.max((subscriptionsQuery.data?.length ?? 1) - 1, 0);

  const handleDelete = async () => {
    if (!trigger) return;
    try {
      await removeTrigger.mutateAsync(trigger.id);
      notification.success?.('Trigger deleted');
      onDeleted();
    } catch (error) {
      Sentry.captureException(error, { extra: { triggerId: trigger.id } });
      notification.error(
        error instanceof Error ? error.message : 'Could not delete the trigger',
      );
    }
  };

  return (
    <DeleteConfirmationModal
      isOpen={!!trigger}
      handleCancel={onClose}
      handleDelete={handleDelete}
      isLoading={removeTrigger.isPending || subscriptionsQuery.isLoading}
      buttonLabel='Delete trigger'
      title='Delete trigger'
      description={
        countUnknown
          ? 'This stops watching the folder entirely and removes it from every workflow attached to it, which may include others besides this one. To leave those running, remove it from this workflow instead.'
          : others > 0
            ? `This stops watching the folder entirely and also removes it from ${others} other workflow${others === 1 ? '' : 's'}. To leave those running, remove it from this workflow instead.`
            : 'This stops watching the folder and deletes the trigger. Runs already in progress are unaffected.'
      }
    />
  );
};
